// src/components/common/Dashboard.jsx
import React, { Suspense } from 'react';
import PropTypes from 'prop-types';
import { Routes, Route, useLocation } from 'react-router-dom';
import GridLayout from '../layout/GridLayout';
import DashboardCard from './DashboardCard';
import LoadingFallback from './LoadingFallback';
import { useColors } from '../../../hooks/useColors';

/**
 * Common Dashboard component for use across different sections
 * @param {Object} props - Component props
 * @param {string} props.title - Dashboard title
 * @param {Array} props.cards - Array of card configurations
 * @param {Array} props.routes - Array of route configurations
 * @param {string} props.basePath - Base path for the dashboard
 * @param {React.ReactNode} props.children - Optional additional content
 */
const Dashboard = ({ 
  title, 
  cards = [], 
  routes = [], 
  basePath = '',
  children 
}) => {
  const { semantic } = useColors();
  const location = useLocation();
  
  // If we're on a route that's not the dashboard index, render the routes
  if (routes.length > 0 && location.pathname !== basePath) {
    return (
      <div className="min-h-screen">
        <Routes>
          {routes.map(({ path, element: Element, requiredRole, isLazy }) => {
            // Restore the original RouteComponent wrapper
            const RouteComponent = () => {
              const ElementComponent = Element;
              return <ElementComponent />;
            };
            
            return (
              <Route 
                key={path} 
                path={path} 
                element={
                  isLazy ? (
                    <Suspense fallback={<LoadingFallback />}>
                      {/* Render the wrapper component */}
                      <RouteComponent /> 
                    </Suspense>
                  ) : (
                    // Render the wrapper component
                    <RouteComponent />
                  )
                } 
              />
            );
          })}
        </Routes>
      </div>
    );
  }

  // Otherwise, render the dashboard cards
  return (
    <GridLayout title={title}>
      {cards.map((card, index) => (
        <DashboardCard
          key={index}
          to={card.to}
          title={card.title}
          description={card.description}
          linkText={card.linkText}
          onClick={card.onClick}
        >
          {card.children}
        </DashboardCard>
      ))}
      {children}
    </GridLayout>
  );
};

Dashboard.propTypes = {
  title: PropTypes.string.isRequired,
  cards: PropTypes.arrayOf(
    PropTypes.shape({
      to: PropTypes.string,
      title: PropTypes.string.isRequired,
      description: PropTypes.string.isRequired,
      linkText: PropTypes.string,
      onClick: PropTypes.func,
      children: PropTypes.node
    })
  ),
  routes: PropTypes.arrayOf(
    PropTypes.shape({
      path: PropTypes.string.isRequired,
      element: PropTypes.elementType.isRequired,
      requiredRole: PropTypes.number,
      isLazy: PropTypes.bool
    })
  ),
  basePath: PropTypes.string,
  children: PropTypes.node
};

export default Dashboard;

